// src/services/cnpj-external/factory.js
// *******************************************************************************
// CNPJ Provider Factory - Multiple providers with fallback
// *******************************************************************************
// Factory pattern for CNPJ lookup with provider abstraction and automatic fallback.
// Providers are tried in order until one succeeds.
//
// Available providers:
// - cnpjws: CNPJ.ws (free, 3 req/min rate limit) - PRIMARY (has IE data)
// - brasilapi: BrasilAPI (free, unlimited, no auth) - FALLBACK
// - jarvisly: Internal database lookup
//
// Based on backend pattern: src/modules/integrations/factory-helpers.js
// *******************************************************************************

import jarvislyAdapter from './jarvisly/adapter.js';
import brasilapiAdapter from './brasilapi/adapter.js';
import cnpjwsAdapter from './cnpjws/adapter.js';
import { createProviderFactory } from '../address/factory-helpers.js';

/**
 * CNPJ Provider registry
 *
 * Order matters for default fallback chain:
 * 1. cnpjws - Primary (has Inscrição Estadual data, 3 req/min limit)
 * 2. brasilapi - Fallback (unlimited, fast, no auth, no IE data)
 * 3. jarvisly - Internal database (for entities already in system)
 */
const CNPJ_REGISTRY = {
  cnpjws: { name: 'cnpjws', adapter: cnpjwsAdapter },
  brasilapi: { name: 'brasilapi', adapter: brasilapiAdapter },
  jarvisly: { name: 'jarvisly', adapter: jarvislyAdapter },
};

/**
 * Creates CNPJ provider instance with automatic fallback
 *
 * @param {Object} [options] - Factory options
 * @param {string|Array<string>} [options.provider] - Provider(s) to use
 *   - If not specified: uses default chain (cnpjws → brasilapi → jarvisly)
 *   - If string: single provider name
 *   - If array: ordered provider names for custom fallback chain
 *
 * @returns {Object} - Provider instance
 * @returns {Array<string>} return.chain - Active provider chain
 * @returns {Object} return.adapter - Composite adapter with fallback logic
 *
 * @example
 * // Use default chain (cnpjws → brasilapi → jarvisly)
 * const { adapter } = getCnpjProvider();
 * const result = await adapter.searchCnpj({ cnpj: '12345678000190' });
 * // result.data contains Universal POJO with entity-aligned structure
 *
 * @example
 * // Use only external APIs (skip internal database)
 * const { adapter } = getCnpjProvider({ provider: ['cnpjws', 'brasilapi'] });
 *
 * @example
 * // Use single provider
 * const { adapter } = getCnpjProvider({ provider: 'brasilapi' });
 *
 * @example
 * // Use internal database only
 * const { adapter } = getCnpjProvider({ provider: 'jarvisly' });
 */
export const getCnpjProvider = createProviderFactory({
  registry: CNPJ_REGISTRY,
  methods: ['searchCnpj'],
  // CNPJ providers may have different data freshness, so try all providers even if one says "not found"
  continueOnNotFound: true,
});

/**
 * Convenience function to get external-only CNPJ provider
 * Uses only external APIs (brasilapi, cnpjws) without internal database
 *
 * @returns {Object} Provider instance configured for external APIs only
 *
 * @example
 * const { adapter } = getExternalCnpjProvider();
 * const result = await adapter.searchCnpj({ cnpj: '12345678000190' });
 */
export const getExternalCnpjProvider = () => {
  return getCnpjProvider({ provider: ['cnpjws', 'brasilapi'] });
};

/**
 * Convenience function to search CNPJ via external APIs
 * This is the main function to use in components for auto-fill functionality
 *
 * @param {string} cnpj - CNPJ number (with or without formatting)
 * @returns {Promise<Object>} Search result with Universal POJO
 * @returns {Object} return.data - Universal POJO with entity-aligned structure
 * @returns {string} return.resolvedName - Provider that returned the result
 *
 * @example
 * const result = await searchCnpjExternal('12345678000190');
 * if (result.data.success) {
 *   // Use result.data to fill form
 *   form.setFieldsValue({
 *     name: result.data.name,
 *     fullName: result.data.fullName,
 *     // ...
 *   });
 * }
 */
export const searchCnpjExternal = async cnpj => {
  const { adapter } = getExternalCnpjProvider();
  return adapter.searchCnpj({ cnpj });
};

export default {
  getCnpjProvider,
  getExternalCnpjProvider,
  searchCnpjExternal,
};
